import { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../lib/api-service';
import toast from 'react-hot-toast';
import { useUIState } from './UIStateContext';

const AuthContext = createContext(null);

// Helper function to decode JWT token
const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const { clearAllState } = useUIState();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on mount (check both storages)
    const storedToken = localStorage.getItem('token') || sessionStorage.getItem('token');
    const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = async (username, password, rememberMe = false) => {
    try {
      const data = await authApi.login(username, password);

      // Decode token to get company name
      const tokenData = decodeToken(data.access_token);

      // Store token based on "Remember me" preference
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem('token', data.access_token);
      setToken(data.access_token);

      // Fetch company details to get logo
      let companyLogo = null;
      try {
        const { companyApi } = await import('../lib/api-service');
        const companyData = await companyApi.getCompany();
        companyLogo = companyData.logo;
      } catch (error) {
        console.error('Failed to fetch company logo:', error);
      }

      // Store user info in the same storage (including company name, logo, roles, and permissions from token)
      const userInfo = {
        username,
        companyName: tokenData?.company_name || 'My Organization',
        companyLogo: companyLogo,
        roles: tokenData?.roles || [],
        permissions: tokenData?.permissions || []
      };
      storage.setItem('user', JSON.stringify(userInfo));
      setUser(userInfo);

      // Also store the storage preference for logout
      localStorage.setItem('rememberMe', rememberMe.toString());

      // Clear UI state on fresh login
      clearAllState();

      // Dispatch custom event to notify WebSocket to reconnect
      window.dispatchEvent(new CustomEvent('userLoggedIn'));

      toast.success(`Welcome back, ${username}!`);
      return { success: true };
    } catch (error) {
      // Error toast is already shown by API interceptor
      return { success: false, error: error.message };
    }
  };

  const register = async (username, email, password, fullName, inviteToken) => {
    try {
      await authApi.register({
        username,
        email,
        password,
        full_name: fullName,
        invite_token: inviteToken,
      });

      // Auto-login after registration
      toast.success('Registration successful!');
      return await login(username, password);
    } catch (error) {
      // Error toast is already shown by API interceptor
      return { success: false, error: error.message };
    }
  };

  const registerOrganization = async (username, password, organizationName, rememberMe = true) => {
    try {
      const data = await authApi.registerOrganization({
        username,
        password,
        organization_name: organizationName || 'My Organization',
      });

      // Decode token to get company name
      const tokenData = decodeToken(data.access_token);

      // Store token based on "Remember me" preference (default to true for new orgs)
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem('token', data.access_token);
      setToken(data.access_token);

      // Fetch company details to get logo
      let companyLogo = null;
      try {
        const { companyApi } = await import('../lib/api-service');
        const companyData = await companyApi.getCompany();
        companyLogo = companyData.logo;
      } catch (error) {
        console.error('Failed to fetch company logo:', error);
      }

      // Store user info (including company name, logo, and roles from token)
      const userInfo = {
        username,
        companyName: tokenData?.company_name || organizationName || 'My Organization',
        companyLogo: companyLogo,
        roles: tokenData?.roles || []
      };
      storage.setItem('user', JSON.stringify(userInfo));
      setUser(userInfo);

      // Store the storage preference
      localStorage.setItem('rememberMe', rememberMe.toString());

      // Clear UI state on fresh login
      clearAllState();

      // Dispatch custom event to notify WebSocket to reconnect
      window.dispatchEvent(new CustomEvent('userLoggedIn'));

      toast.success(`Welcome to ${userInfo.companyName}!`);
      return { success: true };
    } catch (error) {
      // Error toast is already shown by API interceptor
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    // Clear from both storages
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('rememberMe');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');

    setToken(null);
    setUser(null);
    toast.success('Logged out successfully');
  };

  const value = {
    user,
    setUser,
    token,
    loading,
    login,
    register,
    registerOrganization,
    logout,
    isAuthenticated: !!token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
